import type { UserSkillStore } from 'shared/src/user/userKnowledge/user.skill.store';
import QuestionGenerator from './questionGenerator';
import * as Skills from './skills';
import utils from '../utils';
import Note from '../uTheory.Notation/note';
import KeySignature from '../uTheory.Notation/keySignature';

export interface MelodicContourQuestion {
  notes: number[];
  choices: string[];
  answer: string;
  skills: string;
}

const SMALL = 0;
const MEDIUM = 1;
let STEPSORTRIADIC;
const LARGE = 2;
const ANY = 3;

const contourSkills = [
  'contour_basic',
  'contour_high_low',
  'contour_high_medium_low_easy',
  'contour_high_medium_low_hard',
  'contour_four_level_easy',
  'contour_four_level_hard',
];

const completeQuestionBank = {
  '3-note-0': ['012', '210', '000', '010', '101'],
  '3-note-1': ['021', '102', '120', '201', '001', '011', '100', '110'],
  '01': ['0010', '0100', '0101', '0110', '1001', '1010', '1011', '1101', '0000'],
  '012': [
    '0012',
    '0021',
    '0102',
    '0112',
    '0120',
    '0121',
    '0122',
    '0201',
    '0210',
    '0211',
    '0212',
    '0221',
    '1002',
    '1012',
    '1020',
    '1021',
    '1022',
    '1102',
    '1120',
    '1200',
    '1201',
    '1202',
    '1210',
    '1220',
    '2001',
    '2010',
    '2011',
    '2012',
    '2021',
    '2100',
    '2101',
    '2102',
    '2110',
    '2120',
    '2201',
    '2210',
  ],
  '0123': [
    '0123',
    '0132',
    '0213',
    '0231',
    '0312',
    '0321',
    '1023',
    '1032',
    '1203',
    '1230',
    '1302',
    '1320',
    '2013',
    '2031',
    '2103',
    '2130',
    '2301',
    '2310',
    '3012',
    '3021',
    '3102',
    '3120',
    '3201',
    '3210',
  ],
};

export default class MelodicContourQuestions {
  qg: QuestionGenerator = new QuestionGenerator();

  // constructor() {
  //   this.init();
  // }

  // init() {
  //   this.qg = new QuestionGenerator();
  // }

  getQuestions(level: number, numQuestions): MelodicContourQuestion[] {
    return this[level](numQuestions);
  }

  /**
   * Returns recommended training level based on user's skills array
   *
   * @param {SkillWithProgress[]} skillProgressObjects from API, array of skills progress objects: {
   *   id: skillId, int format
   *   score: int -- total score
   *   progress {
   *     accuracy
   *     accuracyScore
   *     questions
   *     questionsScore
   *     speed
   *     speedScore
   *   }
   * }
   * @returns {number} the integer level.
   */
  static getRecommendedLevel(skillProgressObjects: UserSkillStore[]) {
    const skills = utils.clone(contourSkills, true);
    /*
      ... never more than one past last started level...
      ... never more than two past last incomplete level...
      ... if all but last two completed, alternate last two levels, but
          prefer the one whose score is significantly lower.
    */
    const availableSkills = skills
      .map((skill) => {
        const skillProgress = skillProgressObjects.find((s) => s.textId === skill);
        return {
          started: skillProgress.numQuestions,
          completed: skillProgress.score === 100,
          score: skillProgress.score,
          textId: skillProgress.textId,
          id: skillProgress.id,
        };
      })
      .map((skill, index, array) => {
        const priorLevelStarted = array.slice(0, index).every((el) => el.started);
        const withinTwoOfLastCompleted = !array[index - 2] || array[index - 2].completed;
        return {
          ...skill,
          priorLevelStarted,
          withinTwoOfLastCompleted,
        };
      })
      .filter(
        (skill) => skill.priorLevelStarted && skill.withinTwoOfLastCompleted && !skill.completed
      );

    const skillToTrain = availableSkills.length
      ? availableSkills[~~(Math.random() * availableSkills.length)].textId
      : utils.sort(utils.shuffle(skills.slice(-2)), (a, b) =>
          Math.abs(a.score - b.score) >= 10 ? a.score - b.score : 0
        )[0];

    console.log('skillToTrain', skillToTrain);

    return MelodicContourQuestions.skillsToLevels([skillToTrain]);
  }

  /*
   * converts a uTheory skill to a level
   * note, levels are 1-based.
   */
  static skillsToLevels(skills: (string | number)[]): number[] {
    const levels = skills
      .map(Skills.makeString)
      .filter((skill) => contourSkills.indexOf(skill) !== -1)
      .map((skill) => contourSkills.indexOf(skill) + 1)
      .sort((a, b) => (Skills.makeInt(b) || 0) - (Skills.makeInt(a) || 0));

    console.log('levels', levels);

    return levels;
  }

  /*
   * for the first two levels, we use only 3-note long questions.
   * After that, this is parallel to the multiple-choice.
   *
   */
  getDictationQuestions(level: number, numQuestions: number) {
    let questions: MelodicContourQuestion[] = this[level](numQuestions);
    // level 1 should have only 3 notes:
    if (level <= 1) {
      questions = questions.map((q) => {
        q.notes.length = 3;
        q.answer = q.answer.slice(0, 3);

        // Normalize (0-base) the answer, so 321 ===> 210
        const set: Set<string> = new Set(q.answer.split(''));
        const arr = Array.from(set).sort((a, b) => Number(a) - Number(b));

        q.answer = q.answer
          .split('')
          .map((n) => arr.indexOf(n))
          .join('');

        return q;
      });
    }
    return questions;
  }

  static getDecoys(
    questionString: string,
    questionSelector: '3-note-0' | '3-note-1' | '01' | '012' | '0123',
    tricky: boolean
  ): string[] {
    const set: Set<string> = new Set();
    set.add(questionString);
    const questionBank = completeQuestionBank[questionSelector].slice(0);

    // if not tricky, randomly pick within the same group:
    if (!tricky) {
      utils.shuffle(questionBank);
      while (set.size < 4) {
        set.add(questionBank.pop());
      }
      return utils.shuffle(Array.from(set));
    }

    // if tricky:
    // find with the same up down contour from the group:
    let contourMatches = questionBank.filter(profileMatch);
    utils.shuffle(contourMatches);
    // Add the 3's or 4's in if needed:
    const allQ = completeQuestionBank['012'].concat(completeQuestionBank['0123']);
    const otherContourMatches = utils.shuffle(allQ.filter(profileMatch));
    contourMatches = contourMatches.concat(otherContourMatches);

    contourMatches.forEach((match) => {
      if (set.size < 4) {
        set.add(match);
      }
    });

    // fill out missing ones w/ those close by in questionBank:
    if (set.size < 4) {
      const i = questionBank.indexOf(questionString);
      let j = 1;
      while (set.size < 4) {
        if (i + j < questionBank.length) set.add(questionBank[i + j]);
        if (i - j >= 0 && set.size < 4) set.add(questionBank[i - j]);
        j++;
      }
    }

    return utils.shuffle(Array.from(set));

    function profileMatch(a) {
      const b = questionString;

      let profile = '';
      a.split('').reduce(compare);
      const profileA = profile;
      profile = '';
      b.split('').reduce(compare);
      const profileB = profile;
      return profileA === profileB;

      function compare(prior, current) {
        if (prior < current) profile += '-';
        if (prior === current) profile += '=';
        if (prior > current) profile += '+';
        return current;
      }
    }
  }

  /*
   * @param questionString: "1323" or "1243", etc... not scale deg
   *    but rather the relative height of the various notes.
   *
   * @param sizeArray:
   *   small: 2nds -- diatonic or chromatic
   *   medium: 3rds & 4ths
   *   large: 5ths to 8ves
   *   any: mix from the above, but keep diatonic 2nds
   *
   * @param diatonic:
   *   Should the intervals be chosen within a key?
   */
  static questionToNotes(
    questionString: string,
    sizeArray: number[],
    diatonic?: boolean
  ): number[] {
    const max = Number(questionString.split('').sort().slice(-1));
    let intervals: number[] = []; // in set class!!!
    sizeArray.forEach((size) => {
      let selector;
      switch (size) {
        case SMALL:
          intervals = intervals.concat([2]);
          break;
        case MEDIUM:
          intervals = intervals.concat([3, 4]);
          break;
        case LARGE:
          intervals = intervals.concat([5, 6, 7, 8, 9]);
          break;
        case ANY:
          intervals = [2, 3, 4, 5, 6, 7, 8, 9];
          break;
        case STEPSORTRIADIC:
          selector = ~~(Math.random() * 3);
          if (selector === 0) {
            intervals = [2];
            diatonic = true;
          } else if (selector === 1) {
            intervals = [3, 4];
            diatonic = true;
          } else {
            intervals = [2, 3, 4];
          }
          break;
      }
    });

    const midis: number[] = [];
    // If it's only 2nds, pick a random note
    // and build your notes array by randomly going up 1/2 &
    // whole steps from there
    if (utils.equals(intervals, [2]) && !diatonic) {
      const startingNote = Note.randomNote({ highest: 'G5' });
      const midi = startingNote.getMidiNumber();
      midis.push(midi);
      for (let i = 1; i < max; i++) {
        midis.push(midis[midis.length - 1] + (Math.floor(Math.random() * 2) + 1));
      }
      return questionString.split('').map((pitch) => {
        const pitchNumber = Number(pitch);
        return midis[pitchNumber];
      });
    }

    // Otherwise, generate the 3 intervals you need
    // Get the total width of all that
    // And get enough octaves of some random scale
    const intervalMap = [];
    while (intervalMap.length <= max) {
      intervalMap.push(intervals[Math.floor(Math.random() * intervals.length)]);
    }

    const totalGeneric = intervalMap.reduce((a, b) => a - 1 + b);

    let highest = new Note('C6');
    highest = highest.getNoteAtGeneric(-totalGeneric);
    const key = KeySignature.randomKey();
    const note = Note.randomNote({ highest });
    note.accidental = key.getAccidentalAtNote(note);
    const notes = [note];
    intervalMap.forEach((interval) => {
      const newNote = notes[notes.length - 1].getNoteAtGeneric(interval);
      newNote.accidental = key.getAccidentalAtNote(newNote);
      notes.push(newNote);
    });

    // Return the notes array
    const midiNotes = questionString.split('').map((noteIndex) => {
      const noteIndexNumber = Number(noteIndex);
      return notes[noteIndexNumber].getMidiNumber();
    });
    return midiNotes;

    /* DEBUG: In case the problem of repeated notes returns:
    var badQ = (()=>{
     var noteSet =
       new Set(midis);
     var answerSet = new Set(questionString.split(""));
     return answerSet.size !== noteSet.size;
    })();
    var l = (t)=>{console.log(t);};
    if(badQ){
      l("Bad question:");
      l("notes: " + notes + "");
      l("midis: " + midis);
      l("intervalMap: " + intervalMap);
      l("questionString: " + questionString);
    }
  */
  }

  /* Levels:
    contour_basic
    contour_high_low
    contour_high_medium_low_easy
    contour_high_medium_low_hard
    contour_four_level_easy
    contour_four_level_hard
  */

  1(n: number): MelodicContourQuestion[] {
    const questionBank = ['0123', '3210', '0000', '0101'];
    const questions = this.qg.rejectDuplicates(questionBank, n);
    return questions.map((questionString) => ({
      notes: MelodicContourQuestions.questionToNotes(questionString, [LARGE]),
      choices: questionBank,
      answer: questionString,
      skills: 'contour_basic',
    }));
  }

  2(n: number): MelodicContourQuestion[] {
    return this.questionGenerator(n, '01', [ANY], false, 'contour_high_low');
  }

  3(n: number): MelodicContourQuestion[] {
    return this.questionGenerator(
      n,
      '012',
      [STEPSORTRIADIC],
      false,
      'contour_high_medium_low_easy'
    );
  }

  4(n: number): MelodicContourQuestion[] {
    return this.questionGenerator(n, '012', [STEPSORTRIADIC], true, 'contour_high_medium_low_hard');
  }

  5(n: number): MelodicContourQuestion[] {
    return this.questionGenerator(n, '0123', [MEDIUM, LARGE], false, 'contour_four_level_easy');
  }

  6(n: number): MelodicContourQuestion[] {
    return this.questionGenerator(n, '0123', [SMALL, MEDIUM], true, 'contour_four_level_hard');
  }

  /**
   * @param n - number of questions
   * @param questionSelector: string representing pitch level
   *   possibilities (012 -- 3 levels)
   * @param sizeArray [SMALL, MEDIUM, LARGE] -- representing what
   *   kinds of intervals should be used
   * @param tricky -- use decoy answer choices?
   * @param skill -- skill to be added to question object.
   *
   */
  questionGenerator(
    numberOfQuestions: number,
    questionSelector: '3-note-0' | '3-note-1' | '01' | '012' | '0123',
    sizeArray: number[],
    tricky: boolean,
    skill: string
  ): MelodicContourQuestion[] {
    const questionBank = completeQuestionBank[questionSelector];
    const questions: string[] = this.qg.rejectDuplicates(questionBank, numberOfQuestions);
    return questions.map((questionString) => ({
      notes: MelodicContourQuestions.questionToNotes(questionString, sizeArray),
      choices: MelodicContourQuestions.getDecoys(questionString, questionSelector, tricky),
      answer: questionString,
      skills: skill,
    }));
  }
}
